import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePostProfiles, useGetProfilesById } from '@/api/control/control-plane/control-plane';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { LoadingButton } from '@/components/LoadingButton';
import type { CreateProfileRequest } from '@/api/control/missionImpactAPI.schemas';
import { useError } from '@/contexts/ErrorContext';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { getAvailableRegions, getRegionLabel } from '@/constants/regions';
import { HELP_MENU_PREFERENCE_KEY } from '@/contexts/HelpMenuContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';

/**
 * Step 1: Create Profile or View Profile
 * First step in the profile creation wizard
 * Supports both create mode (no profileId) and view/edit mode (with profileId)
 */
export default function CreateProfileStep() {
  const navigate = useNavigate();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const { orgId, profileId } = useParams<{ orgId: string; profileId?: string }>();
  const { orgSettings } = useOrgs();

  if (!orgId) {
    logger.error(new Error('No orgId found in route params'), {
      context: 'CreateProfileStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const isEditMode = !!profileId;
  const availableRegions = getAvailableRegions();

  // Determine default region: org setting > first available > eastus fallback
  const getDefaultRegion = () => {
    if (orgSettings?.defaultRegion) {
      return orgSettings.defaultRegion;
    }
    return availableRegions[0] ?? 'eastus';
  };

  const [formData, setFormData] = useState<CreateProfileRequest>({
    name: '',
    regionId: getDefaultRegion(),
    isEnabled: false,
  });

  // Fetch profile data if in edit mode
  const { data: profileData, isLoading: isLoadingProfile } = useGetProfilesById(
    orgId,
    profileId ?? '',
    {
      query: {
        enabled: isEditMode && !!profileId,
      },
    }
  );

  const profile = profileData?.data;

  // Update form data when profile is loaded (edit mode)
  useEffect(() => {
    if (isEditMode && profile) {
      setFormData({
        name: profile.name,
        regionId: profile.regionId,
        isEnabled: profile.isEnabled,
      });
    }
  }, [isEditMode, profile]);

  // Update default region when org settings load (create mode only)
  useEffect(() => {
    if (!isEditMode && orgSettings?.defaultRegion && !formData.name) {
      // Only update if form hasn't been touched (name is still empty)
      setFormData((prev) => ({
        ...prev,
        regionId: orgSettings.defaultRegion,
      }));
    }
  }, [isEditMode, orgSettings?.defaultRegion, formData.name]);

  // Determine wizard steps based on mode
  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.PROFILE, {
    [WIZARD_STEP_IDS.PROFILE]: isEditMode,
    [WIZARD_STEP_IDS.SOURCE]: isEditMode && !!profile?.source,
    [WIZARD_STEP_IDS.TARGET]: isEditMode && !!profile?.target,
  });

  const { mutate: createProfile, isPending: isCreating } = usePostProfiles({
    mutation: {
      onSuccess: (response) => {
        const newProfileId = response.data.profileId ?? '';
        toast.success(t.wizard.profile.profileCreatedSuccessfully);
        logger.trackEvent('Profile.Created', {
          orgId,
          profileId: newProfileId,
          regionId: formData.regionId,
          isEnabled: formData.isEnabled,
        });

        // Reset help menu preference so it will show again for the new profile
        localStorage.setItem(HELP_MENU_PREFERENCE_KEY, 'true');

        void navigate(`/dashboard/orgs/${orgId}/profiles/${newProfileId}/setup/source`);
      },
      onError: (error) => {
        handleApiError(error, { context: 'CreateProfileStep.createProfile', profile: profileData?.data });
      },
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast.error(t.wizard.profile.pleaseEnterProfileName);
      return;
    }

    createProfile({ orgId, data: formData });
  };

  // Show loading state when fetching profile
  if (isEditMode && isLoadingProfile) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Redirect if profile not found in edit mode
  if (isEditMode && !isLoadingProfile && !profile) {
    void navigate(`/dashboard/orgs/${orgId}/profiles`);
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">{isEditMode ? t.wizard.profile.titleEdit : t.wizard.profile.title}</h1>
        <p className="text-muted-foreground">
          {isEditMode ? t.wizard.profile.descriptionEdit : t.wizard.profile.description}
        </p>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.profile.cardTitle}</CardTitle>
          <CardDescription>{t.wizard.profile.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Name */}
            <div className="space-y-2">
              <Label htmlFor="name">{t.wizard.profile.profileName}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => { setFormData({ ...formData, name: e.target.value }); }}
                placeholder={t.wizard.profile.profileNamePlaceholder}
                required
                disabled={isEditMode}
                readOnly={isEditMode}
              />
            </div>

            {/* Region Selection */}
            <div className="space-y-2">
              <Label htmlFor="region">{t.wizard.profile.region}</Label>
              <Select
                value={formData.regionId}
                onValueChange={(value) => { setFormData({ ...formData, regionId: value }); }}
                disabled={isEditMode}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t.wizard.profile.regionPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {availableRegions.map((region) => (
                    <SelectItem key={region} value={region}>
                      {getRegionLabel(region)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Enable Toggle */}

            {/* Actions */}
            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (isEditMode && profileId) {
                    // In edit mode, back goes to profiles list
                    void navigate(`/dashboard/orgs/${orgId}/profiles`);
                  } else {
                    // In create mode, cancel goes to profiles list
                    void navigate(`/dashboard/orgs/${orgId}/profiles`);
                  }
                }}
              >
                {isEditMode ? t.wizard.common.back : t.wizard.common.cancel}
              </Button>
              {!isEditMode && (
                <LoadingButton type="submit" disabled={!formData.name?.trim()} isLoading={isCreating}>
                  {t.wizard.profile.nextConfigureSource}
                </LoadingButton>
              )}
              {isEditMode && profileId && (
                <Button
                  type="button"
                  onClick={() => {
                    void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`);
                  }}
                >
                  {t.wizard.profile.nextConfigureSource}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
